import React, { useState, useEffect } from 'react';
import { X, PenLine } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import api from '../lib/api';
import { getPetName } from '../lib/petUtils';

interface Student {
  id: number;
  name: string;
  petType: string | null;
  nickname?: string | null;
}

interface RenamePetModalProps {
  isOpen: boolean;
  onClose: () => void;
  student: Student | null;
  onSuccess: () => void;
}

export default function RenamePetModal({
  isOpen,
  onClose,
  student,
  onSuccess,
}: RenamePetModalProps) {
  const { t } = useTranslation();
  const [nickname, setNickname] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && student) {
      setNickname(student.nickname || '');
    }
  }, [isOpen, student]);

  if (!isOpen || !student) return null;

  const handleSubmit = async () => {
    // Basic validation
    if (nickname.length > 12) {
        // We could show an error here, but the input max length handles it mostly
        return;
    }

    setLoading(true);
    try {
      await api.put(`/students/${student.id}`, { nickname });
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Failed to rename pet:', error);
    } finally {
      setLoading(false);
    }
  };

  const petName = student.petType ? getPetName(student.petType) : '';

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/40 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      ></div>

      {/* Modal Content */}
      <div className="relative bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in-95 duration-200 flex flex-col">
        
        {/* Header - Light Gradient to match StudentActionModal */}
        <div className="bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 text-gray-900 p-6 relative overflow-hidden">
           {/* Decorative circles */}
           <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-200/40 rounded-full -translate-y-1/2 translate-x-1/3 blur-2xl pointer-events-none"></div>
           <div className="absolute bottom-0 left-0 w-24 h-24 bg-purple-200/35 rounded-full translate-y-1/2 -translate-x-1/3 blur-xl pointer-events-none"></div>

           <button 
            onClick={onClose}
            className="absolute right-4 top-4 p-2 hover:bg-gray-900/5 rounded-full text-gray-400 hover:text-gray-900 transition-colors z-20"
          >
            <X className="w-6 h-6" />
          </button>

          <div className="flex items-center gap-3 mb-1 relative z-10">
            <div className="w-12 h-12 rounded-2xl bg-white flex items-center justify-center shadow-sm text-indigo-500">
                <PenLine className="w-6 h-6" />
            </div>
            <div>
                <h2 className="text-2xl font-black text-gray-800">{t('renamePet.title')}</h2>
                <p className="text-gray-500 text-sm font-bold">
                    {t('renamePet.subtitle', { studentName: student.name, petName })}
                </p>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="p-6 md:p-8">
            <div className="mb-8">
                <label className="block text-gray-800 font-bold mb-3 text-lg">
                    {t('renamePet.label')}
                </label>
                <input
                    type="text"
                    value={nickname}
                    onChange={(e) => setNickname(e.target.value)}
                    placeholder={t('renamePet.placeholder')}
                    maxLength={12}
                    className="w-full px-4 py-3 rounded-xl border-2 border-indigo-200 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 outline-none transition-all text-gray-800 font-bold text-lg placeholder:text-gray-300 placeholder:font-normal"
                    autoFocus
                />
                <p className="mt-2 text-gray-400 text-xs">
                    {t('renamePet.hint')}
                </p>
            </div>

            <div className="flex gap-3">
                <button
                    onClick={onClose}
                    className="flex-1 py-3 rounded-xl border-2 border-gray-100 text-gray-500 font-bold hover:bg-gray-50 transition-colors"
                >
                    {t('renamePet.cancel')}
                </button>
                <button
                    onClick={handleSubmit}
                    disabled={loading}
                    className="flex-1 py-3 rounded-xl bg-indigo-600 text-white font-bold hover:bg-indigo-700 shadow-lg shadow-indigo-200 transition-all disabled:opacity-70 disabled:cursor-not-allowed"
                >
                    {loading ? '...' : t('renamePet.save')}
                </button>
            </div>
        </div>
      </div>
    </div>
  );
}
